import React from 'react'
import {
  HStack,
  Circle,
  Box,
  Text as ChakraText,
  Divider,
} from '@chakra-ui/react'
import { useTranslation } from 'react-i18next'
import numeral from 'numeral'
import type { IconType } from 'react-icons'
import { MdOutlineMarkChatUnread, MdVideoSettings } from 'react-icons/md'
import { CgExtension } from 'react-icons/cg'
import { SiSpeedtest } from 'react-icons/si'
import type {
  AnalyticsAPIResponse,
  BandwidthAndRequest,
  ExtensionUnits,
  VideoProcessingUnits,
} from '../../interfaces'

interface Props {
  data: AnalyticsAPIResponse
}

const UsageStatisticBox = ({
  IconComponent,
  heading,
  text,
}: {
  IconComponent: IconType
  heading: string
  text: string
}): React.ReactElement => (
  <Box
    spacing="1em"
    padding="1.5em"
    flex="1"
    borderRadius="16px"
    bgColor="#f7fafc"
  >
    <HStack alignItems="center" spacing="0.5em">
      <Circle bgColor="white" size="3rem">
        <IconComponent size="1.5rem" color="#2c93fa" />
      </Circle>
      <ChakraText fontWeight="semibold" color="#1a202c" fontSize="1.4rem">
        {heading}
      </ChakraText>
    </HStack>

    <Box marginTop="10px">
      <ChakraText color="#4a5568" fontSize="13px">
        {text}
      </ChakraText>
    </Box>
  </Box>
)

const TotalStat = ({ data }: Props): React.ReactElement => {
  const { t } = useTranslation()

  const {
    bandwidth = {} as BandwidthAndRequest,
    request = {} as BandwidthAndRequest,
    extensionUnits = {} as ExtensionUnits,
    videoProcessingUnits = {} as VideoProcessingUnits,
    storage_bytes = {} as BandwidthAndRequest,
    original_cache_storage_bytes= {} as BandwidthAndRequest,
  } = data
  const { total: bandwidthTotal = 0 } = bandwidth
  const { total: requestTotal = 0 } = request
  const { total: mediaLibraryStorageBytes = 0 } = storage_bytes
  const { total: originalCacheStorageBytes = 0 } = original_cache_storage_bytes
  const { total: extensionUnitsTotal = 0 } = extensionUnits
  const { total: videoProcessingUnitsTotal = 0 } = videoProcessingUnits

  return (
    <Box width="100%">
      {/* <ChakraText fontWeight="semibold" marginBottom="0.75rem" fontSize="16px">
        {t('usage_statistics', 'Usage Statistics')}
      </ChakraText> */}
      {/* <Divider /> */}

      <HStack width="full" spacing="1em" marginTop="1rem">
        <UsageStatisticBox
          IconComponent={MdOutlineMarkChatUnread}
          heading={numeral(bandwidthTotal).format('0.0 ib').replace('i', '')}
          text={t('bandwidth_title_case', 'Bandwidth')}
        />

        <UsageStatisticBox
          IconComponent={SiSpeedtest}
          heading={numeral(requestTotal).format('0.0 a')}
          text={t('requests_title_case', 'Requests')}
        />

        <UsageStatisticBox
          IconComponent={MdVideoSettings}
          heading={numeral(videoProcessingUnitsTotal).format('0 a')}
          text={t(
            'video_processing_units_title_case',
            'Video processing units',
          )}
        />
      </HStack>

      <HStack width="full" spacing="1em" marginTop="1rem">
        <UsageStatisticBox
          IconComponent={CgExtension}
          heading={numeral(extensionUnitsTotal).format('0 a')}
          text={t('extension_units_title_case', 'Extension units')}
        />

        <UsageStatisticBox
          IconComponent={MdOutlineMarkChatUnread}
          heading={numeral(mediaLibraryStorageBytes).format('0.0 ib').replace('i', '')}
          text={"Current Media library storage"}
        />

        <UsageStatisticBox
          IconComponent={MdOutlineMarkChatUnread}
          heading={numeral(originalCacheStorageBytes).format('0.0 ib').replace('i', '')}
          text={"Current Original cache storage"}
        />
      </HStack>
    </Box>
  )
}

export default TotalStat
